import h from 'hyperscript';

import 'ui/progress-bar/progress-bar';
import { Modal } from 'ui/dialog/modal/modal';
import { AlertDialog } from 'ui/dialog/alert/alert';

import {
  deleteWorkOrders,
  getWorkOrderResultUrl,
  observeForResult,
  WorkOrderCanceledError
} from 'cadenza/api-client/workorder-api';
import { delay } from 'cadenza/utils/promise-utils';

import exportIcon from '@disy/cadenza-icons/export.svg';

import { icon } from 'cadenza/utils/icon/icon';
import { downloadImmediately } from 'cadenza/utils/download';
import { logger } from 'cadenza/utils/logging';

import './file-export-progress-modal.css';
import i18n from './file-export-progress-modal.properties';

const RESULT_EVENT_NAME = 'file-ready-for-download';

const COMPONENT_NAME = 'd-file-export-progress-modal';

export class FileExportProgressModal extends Modal {

  _workOrderId = null;

  _startExport = null;

  _progressBar = h('d-progress-bar', {
    attrs: {
      value: 0
    }
  });

  _errorMessageMapper;
  _progressMessageContainer;
  _progressMessageMapper;

  /**
   * When you want to start the process of export, you do that by calling this method: startExportAndShowProgress().
   * This dialog assumes that the client will wait for the event 'file-ready-for-download'. Upon receiving
   * that event, the download of the file will start. Location on which the file is located is also
   * predetermined, it should be here: /workOrder/${workOrderId}/results/file
   *
   * @param {string} dialogTitle
   * @param {Function} startExport - This function shouldn't have any arguments and should return workOrderId.
   *   This function is used to start process of monitoring progress of workOrder.
   * @param {Function} errorMessageMapper - function that can map error to expected error message that should be shown to the user
   * @param {Function} progressMessageMapper - function that can map progress step key to expected progress message that should be shown to the user
   * @param {HTMLElement} exportDescriptionElement - additional element that can be placed below progress bar
   * @param closeAll - closes all parent dialogs on closing
   */
  constructor (dialogTitle, startExport, errorMessageMapper = () => null, progressMessageMapper = () => null, exportDescriptionElement = null, closeAll = false) {
    super({ title: dialogTitle, titleIcon: icon(exportIcon), cancelOnBackdropClick: false });
    this._errorMessageMapper = errorMessageMapper;
    this._progressMessageMapper = progressMessageMapper;
    this.dialog.classList.add(COMPONENT_NAME);

    this.body.classList.add('d-stack-v', 'space-3');
    this.body.append(this._progressBar);

    if (exportDescriptionElement) {
      this._progressMessageContainer = h(`.${COMPONENT_NAME}--progress-message-container`);
      this.body.append(this._progressMessageContainer);
      this._progressMessageContainer.append(exportDescriptionElement);
    }
    this.body.append(h('p', i18n('info.downloadWillStartAutomatically')));

    this.addCancelButton();

    this._startExport = startExport;
    this.connectAndShow({ closeAll });
  }

  startExportAndShowProgress () {
    return delay(this._startExport()
      .then(workOrderId => {
        this._workOrderId = workOrderId;
        return observeForResult(workOrderId, RESULT_EVENT_NAME)
          .progress(({ progress, step }) => {
            this._updateProgress(progress, step);
          });
      }))
      .then(() => {
        downloadImmediately(this._getExportedFileUrl());
        super.close();
      })
      .catch((error) => {
        if (!(error instanceof WorkOrderCanceledError)) {
          logger.error('Export failed', error);
          this.showErrorMessage(this._errorMessageMapper(error));
        }
      });
  }

  _getExportedFileUrl () {
    return getWorkOrderResultUrl(this._workOrderId, 'file');
  }

  _cancelExport () {
    deleteWorkOrders([ this._workOrderId ])
      .then(() => {
        super.closeAll();
      })
      .catch((error) => {
        logger.error('Export failed', error);
        this.showErrorMessage(this._errorMessageMapper(error));
      });
  }

  cancel () {
    this._cancelExport();
  }

  _updateProgress (value, step) {
    this._progressBar.value = value;
    const progressMessage = this._progressMessageMapper(step);
    if (this._progressMessageContainer && progressMessage) {
      this._progressMessageContainer.innerHTML = '';
      this._progressMessageContainer.append(h('span', progressMessage));
    }
  }

  showErrorMessage (errorMessage) {
    const messageToShow = errorMessage ?? {
      title: i18n('errorMessage.fileGenerationFailedTitle'),
      detail: i18n('errorMessage.fileGenerationFailedDescription')
    };

    const errorDialog = new AlertDialog(messageToShow.title, {
      type: 'error',
      message: messageToShow.detail,
      submitCallback: () => { this.closeAll(); }
    }
    );
    errorDialog.connectAndShow();
  }

}

customElements.define(COMPONENT_NAME, FileExportProgressModal);
